// frontend/src/admin/pages/SupportTickets.js
import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Search,
  Eye,
  CheckCircle,
  X,
  User,
  Tag,
  AlertCircle
} from 'lucide-react';
import '../styles/SupportTickets.css';

// ✅ utilisation du client Axios commun
import api from '../../services/api';

const SupportTickets = ({ adminUser, onPageChange }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    onPageChange?.('support');
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadTickets = async () => {
    try {
      setLoading(true);

      const response = await api.get('/support/admin/tickets', {
        params: {
          status: filters.status,
          category: filters.category,
          priority: filters.priority
        }
      });

      const data = response.data;
      console.log('📦 Données tickets reçues:', data);

      if (data.success) {
        const list = Array.isArray(data.tickets)
          ? data.tickets
          : Array.isArray(data.rows)
          ? data.rows
          : [];

        const normalized = list.map((t) => ({
          ...t,
          subject: t.subject || '',
          message: t.message || 'Aucun message',
          user_email: t.user_email || t.user_email_current || ''
        }));

        setTickets(normalized);
      } else {
        console.error('❌ Erreur API tickets:', data.message);
        setTickets([]);
      }
    } catch (error) {
      console.error('❌ Erreur chargement tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const response = await api.put(`/support/admin/ticket/${ticketId}/status`, {
        status: newStatus
      });

      const data = response.data;

      if (data.success) {
        await loadTickets();
        setSelectedTicket(null);
        alert('✅ Statut mis à jour avec succès');
      } else {
        alert(`❌ Erreur: ${data.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('❌ Erreur mise à jour statut:', error);
      alert('❌ Erreur lors de la mise à jour');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      bug_report: '🐛',
      feature_request: '💡',
      user_report: '🚨',
      support_question: '❓',
      payment_issue: '💳',
      other: '📢'
    };
    return icons[category] || '📝';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      bug_report: 'Bug',
      feature_request: 'Amélioration',
      user_report: 'Signalement utilisateur',
      support_question: 'Question',
      payment_issue: 'Paiement',
      other: 'Autre'
    };
    return labels[category] || category;
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'Nouveau',
      in_progress: 'En cours',
      waiting_user: 'Attente utilisateur',
      resolved: 'Résolu',
      closed: 'Fermé'
    };
    return labels[status] || status;
  };

  const filteredTickets = (Array.isArray(tickets) ? tickets : []).filter((ticket) => {
    const matchSearch =
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.user_email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch;
  });

  if (loading) {
    return (
      <div className="admin-support-loading">
        <div className="admin-loading-spinner"></div>
        <h2>Chargement des tickets...</h2>
      </div>
    );
  }

  return (
    <div className="admin-support-page">
      {/* Header */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <MessageSquare size={32} />
          <div>
            <h1>Support Tickets</h1>
            <p>{tickets.length} tickets au total</p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="admin-filters-panel open">
        <div className="admin-search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher par sujet ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-search-input"
          />
        </div>

        <div className="admin-filters-row">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="admin-filter-select"
          >
            <option value="">Toutes catégories</option>
            <option value="bug_report">🐛 Bug</option>
            <option value="feature_request">💡 Amélioration</option>
            <option value="user_report">🚨 Signalement</option>
            <option value="support_question">❓ Question</option>
            <option value="payment_issue">💳 Paiement</option>
            <option value="other">📢 Autre</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="admin-filter-select"
          >
            <option value="">Toutes priorités</option>
            <option value="urgent">Urgent</option>
            <option value="high">Haute</option>
            <option value="normal">Normale</option>
            <option value="low">Basse</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="admin-filter-select"
          >
            <option value="">Tous statuts</option>
            <option value="new">Nouveau</option>
            <option value="in_progress">En cours</option>
            <option value="waiting_user">Attente utilisateur</option>
            <option value="resolved">Résolu</option>
            <option value="closed">Fermé</option>
          </select>
        </div>
      </div>

      {/* Liste des tickets */}
      <div className="admin-tickets-grid">
        {filteredTickets.map((ticket) => (
          <div key={ticket.id} className="admin-ticket-card">
            <div className="ticket-header">
              <div className="ticket-badges">
                <span className={`category-badge category-${ticket.category}`}>
                  {getCategoryIcon(ticket.category)} {getCategoryLabel(ticket.category)}
                </span>
                <span className={`priority-badge priority-${ticket.priority}`}>
                  {ticket.priority}
                </span>
              </div>
              <span className={`status-badge status-${ticket.status}`}>
                {getStatusLabel(ticket.status)}
              </span>
            </div>

            <h3 className="ticket-subject">{ticket.subject}</h3>

            {/* Aperçu du message (3 lignes max) */}
            <div className="ticket-message-preview">
              <p>{ticket.message}</p>
            </div>

            <div className="ticket-meta">
              <p>
                <strong>De:</strong> {ticket.user_email}
              </p>
              <p>
                <strong>Ticket:</strong> #{ticket.ticket_number}
              </p>
              <p>
                <strong>Créé:</strong>{' '}
                {ticket.created_at
                  ? new Date(ticket.created_at).toLocaleDateString('fr-FR')
                  : 'N/A'}
              </p>
            </div>

            <div className="ticket-actions">
              <button
                className="btn-view"
                onClick={() => setSelectedTicket(ticket)}
              >
                <Eye size={16} /> Voir détails
              </button>

              {ticket.status !== 'resolved' && (
                <button
                  className="btn-resolve"
                  onClick={() => updateTicketStatus(ticket.id, 'resolved')}
                >
                  <CheckCircle size={16} /> Résoudre
                </button>
              )}
            </div>
          </div>
        ))}

        {filteredTickets.length === 0 && (
          <div className="admin-no-tickets">
            <MessageSquare size={48} />
            <h3>Aucun ticket trouvé</h3>
            <p>Modifiez vos filtres de recherche</p>
          </div>
        )}
      </div>

      {/* Modal détails complets */}
      {selectedTicket && (
        <div
          className="ticket-modal-overlay"
          onClick={() => setSelectedTicket(null)}
        >
          <div
            className="ticket-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ticket-modal-header">
              <div>
                <h2>{selectedTicket.subject}</h2>
                <div className="modal-badges">
                  <span className={`category-badge category-${selectedTicket.category}`}>
                    {getCategoryIcon(selectedTicket.category)}{' '}
                    {getCategoryLabel(selectedTicket.category)}
                  </span>
                  <span className={`priority-badge priority-${selectedTicket.priority}`}>
                    {selectedTicket.priority}
                  </span>
                  <span className={`status-badge status-${selectedTicket.status}`}>
                    {getStatusLabel(selectedTicket.status)}
                  </span>
                </div>
              </div>
              <button
                className="modal-close"
                onClick={() => setSelectedTicket(null)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="ticket-modal-body">
              {/* Infos utilisateur */}
              <div className="modal-section">
                <h3>
                  <User size={18} /> Utilisateur
                </h3>
                <div className="modal-info-grid">
                  <div>
                    <strong>Email:</strong>
                    <p>{selectedTicket.user_email}</p>
                  </div>
                  <div>
                    <strong>Username:</strong>
                    <p>{selectedTicket.username || 'N/A'}</p>
                  </div>
                  <div>
                    <strong>User Agent:</strong>
                    <p style={{ fontSize: '12px', color: '#6b7280' }}>
                      {selectedTicket.user_agent || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <strong>IP:</strong>
                    <p>{selectedTicket.ip_address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Message complet */}
              <div className="modal-section">
                <h3>
                  <MessageSquare size={18} /> Message complet
                </h3>
                <div className="modal-message-full">
                  {selectedTicket.message}
                </div>
              </div>

              {/* Infos ticket */}
              <div className="modal-section">
                <h3>
                  <Tag size={18} /> Informations
                </h3>
                <div className="modal-info-grid">
                  <div>
                    <strong>N° Ticket:</strong>
                    <p>#{selectedTicket.ticket_number}</p>
                  </div>
                  <div>
                    <strong>Créé le:</strong>
                    <p>
                      {selectedTicket.created_at
                        ? new Date(selectedTicket.created_at).toLocaleString('fr-FR')
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <strong>Dernière mise à jour:</strong>
                    <p>
                      {selectedTicket.updated_at
                        ? new Date(selectedTicket.updated_at).toLocaleString('fr-FR')
                        : 'N/A'}
                    </p>
                  </div>
                  {selectedTicket.resolved_at && (
                    <div>
                      <strong>Résolu le:</strong>
                      <p>
                        {new Date(selectedTicket.resolved_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Réponse admin */}
              {selectedTicket.admin_response && (
                <div className="modal-section">
                  <h3>
                    <AlertCircle size={18} /> Réponse admin
                  </h3>
                  <div className="admin-response-box">
                    {selectedTicket.admin_response}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="modal-actions">
                {selectedTicket.status !== 'resolved' && (
                  <button
                    className="btn-modal-resolve"
                    onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                  >
                    <CheckCircle size={18} /> Marquer comme résolu
                  </button>
                )}

                {selectedTicket.status === 'new' && (
                  <button
                    className="btn-modal-progress"
                    onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')}
                  >
                    En cours de traitement
                  </button>
                )}

                <button
                  className="btn-modal-close"
                  onClick={() => setSelectedTicket(null)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
